import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Customer, CustomerStation, StationContact, CustomerDocument, TopDepartment } from '../models/customer.model';

@Injectable({ providedIn: 'root' })
export class CustomerService {
  private readonly url = `${environment.apiUrl}/customers`;
  private readonly nocache = new HttpHeaders({ 'Cache-Control': 'no-cache' });

  constructor(private http: HttpClient) {}

  // ── Customers ─────────────────────────────────────────
  list(search?: string, customerType?: string): Observable<Customer[]> {
    let p = new HttpParams();
    if (search) p = p.set('search', search);
    if (customerType) p = p.set('customerType', customerType);
    return this.http.get<Customer[]>(this.url, { params: p, headers: this.nocache });
  }

  /** Customers ranked by a blended frequency + recency score (for booking-form chips). */
  top(type: 'institution' | 'privat' = 'institution', limit = 10): Observable<Customer[]> {
    const p = new HttpParams().set('limit', String(limit)).set('type', type);
    return this.http.get<Customer[]>(`${this.url}/top`, { params: p, headers: this.nocache });
  }

  get(id: string): Observable<Customer> {
    return this.http.get<Customer>(`${this.url}/${id}`, { headers: this.nocache });
  }

  create(c: Partial<Customer>): Observable<Customer> {
    return this.http.post<Customer>(this.url, c);
  }

  update(id: string, c: Partial<Customer>): Observable<Customer> {
    return this.http.put<Customer>(`${this.url}/${id}`, c);
  }

  delete(id: string): Observable<any> {
    return this.http.delete(`${this.url}/${id}`);
  }

  // ── Stations ──────────────────────────────────────────
  /** Most-used departments by booking usage (with owning company) — for the "Department" chips. */
  topDepartments(limit = 10): Observable<TopDepartment[]> {
    const p = new HttpParams().set('limit', String(limit));
    return this.http.get<TopDepartment[]>(`${this.url}/stations/top`, { params: p, headers: this.nocache });
  }

  /** All distinct department names defined on companies — fallback for the department chips. */
  allDepartments(): Observable<string[]> {
    return this.http.get<string[]>(`${this.url}/stations/all`, { headers: this.nocache });
  }

  /** A company's stations (departments) ranked by booking usage — for the booking-form chips. */
  topStations(customerId: string, limit = 10): Observable<CustomerStation[]> {
    const p = new HttpParams().set('limit', String(limit));
    return this.http.get<CustomerStation[]>(`${this.url}/${customerId}/stations/top`, { params: p, headers: this.nocache });
  }

  createStation(customerId: string, s: { name: string; address: string }): Observable<CustomerStation> {
    return this.http.post<CustomerStation>(`${this.url}/${customerId}/stations`, s);
  }

  updateStation(customerId: string, stationId: string, s: { name: string; address: string }): Observable<CustomerStation> {
    return this.http.put<CustomerStation>(`${this.url}/${customerId}/stations/${stationId}`, s);
  }

  deleteStation(customerId: string, stationId: string): Observable<any> {
    return this.http.delete(`${this.url}/${customerId}/stations/${stationId}`);
  }

  // ── Station contacts ──────────────────────────────────
  createContact(customerId: string, stationId: string, c: Partial<StationContact>): Observable<StationContact> {
    return this.http.post<StationContact>(`${this.url}/${customerId}/stations/${stationId}/contacts`, c);
  }

  updateContact(customerId: string, stationId: string, contactId: string, c: Partial<StationContact>): Observable<StationContact> {
    return this.http.put<StationContact>(`${this.url}/${customerId}/stations/${stationId}/contacts/${contactId}`, c);
  }

  deleteContact(customerId: string, stationId: string, contactId: string): Observable<any> {
    return this.http.delete(`${this.url}/${customerId}/stations/${stationId}/contacts/${contactId}`);
  }

  // ── Documents ─────────────────────────────────────────
  getDocuments(customerId: string): Observable<CustomerDocument[]> {
    return this.http.get<CustomerDocument[]>(`${this.url}/${customerId}/documents`, { headers: this.nocache });
  }

  uploadDocument(customerId: string, file: File): Observable<CustomerDocument> {
    const fd = new FormData();
    fd.append('file', file);
    return this.http.post<CustomerDocument>(`${this.url}/${customerId}/documents`, fd);
  }

  deleteDocument(customerId: string, docId: string): Observable<any> {
    return this.http.delete(`${this.url}/${customerId}/documents/${docId}`);
  }
}
